import { Button } from "./ui/button";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import {
  Coins,
  TrendingUp,
  Clock,
  ArrowRight,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import {
  useClaimCampaignsApiCallMutation,
  useGetCampaignsDashboardApiCallQuery,
  useGetRecommendedCampaignsApiCallQuery,
} from "@/services/api/campaigns/campaigns.api";
import { data, Link } from "react-router-dom";
import { useGetTransactionsApiCallQuery } from "@/services/api/transactions/transactions.api";
import { getTimeRemaining } from "@/utils/getTimeRemaining";
import { DASHBOARD_PATH } from "@/constants/constant";
import Loader from "./Loader";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

export default function DashboardOverview() {
  const [claimRewardsCall, { isLoading: isClaiming }] =
    useClaimCampaignsApiCallMutation();

  const claimRewards = async (id: string) => {
    try {
      const response = await claimRewardsCall({ campaignId: id }).unwrap();
      if (response?.responseCode === 200) {
        toast.success(response?.responseMessage);
      } else {
        toast.error(response?.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as { responseMessage?: string };
      toast.error(errorData?.responseMessage || "Error claiming rewards");
    }
  };

  const userStats = {
    totalClaimed: "2,450",
    pendingRewards: "750",
    activeCampaigns: 3,
    completedCampaigns: 2,
  };

  const activeCampaigns = [
    {
      id: 1,
      name: "BINGOLD Genesis Launch",
      progress: 75,
      tasksCompleted: 3,
      totalTasks: 4,
      reward: "500",
      timeLeft: "5 days",
      status: "active",
    },
    {
      id: 2,
      name: "Community Builder Campaign",
      progress: 100,
      tasksCompleted: 4,
      totalTasks: 4,
      reward: "250",
      timeLeft: "12 days",
      status: "claimable",
    },
    {
      id: 3,
      name: "Early Adopter Rewards",
      progress: 67,
      tasksCompleted: 2,
      totalTasks: 3,
      reward: "400",
      timeLeft: "3 days",
      status: "active",
    },
  ];

  const recommendedCampaigns = [
    {
      id: 4,
      name: "Referral Program",
      reward: "150",
      tasks: 2,
      participants: "6.7K",
    },
    {
      id: 5,
      name: "Content Creator Program",
      reward: "200",
      tasks: 3,
      participants: "9.1K",
    },
  ];

  const { data: campaignsData, isLoading } =
    useGetCampaignsDashboardApiCallQuery();
  const { data: transactionsData, isError: transactionsError } =
    useGetTransactionsApiCallQuery({
      transactionType: "JOINED",
      limit: 5,
      page: 1,
      campaignType: "Public",
    });

  const { data: recommendedCampaignsData, isError: recommendedCampaignsError } =
    useGetRecommendedCampaignsApiCallQuery();

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex items-center justify-center z-50">
        <Loader size="lg" text="Loading campaign details..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl text-white mb-2">
            Welcome back! 👋
          </h1>
          <p className="text-gray-400">Here's your BINGOLD earning progress</p>
        </div>
        <Link to={DASHBOARD_PATH?.ALL_CAMPAIGNS} className="cursor-pointer">
          <Button className="bg-gradient-to-r cursor-pointer from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 mt-4 md:mt-0">
            View All Campaigns
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Rewards Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-400 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.totalClaimed}
                </div>
                <div className="text-gray-400 text-sm">BINGOLD Claimed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.totalPendingClaim}
                </div>
                <div className="text-gray-400 text-sm">Pending Rewards</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.activeCampaigns}
                </div>
                <div className="text-gray-400 text-sm">Active Campaigns</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-yellow-500/20">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-purple-400 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div className="min-w-0">
                <div className="text-xl md:text-2xl text-white font-semibold">
                  {campaignsData?.result?.completedCampaigns}
                </div>
                <div className="text-gray-400 text-sm">Completed</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Active Campaigns */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl text-white">
            My Active Campaigns
          </h2>
          <Link to={DASHBOARD_PATH?.ACTIVE_CAMPAIGNS}>
            <Button
              variant="outline"
              className="cursor-pointer border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black mt-4 md:mt-0"
            >
              View All
            </Button>
          </Link>
        </div>

        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {transactionsData?.result?.docs &&
              transactionsData?.result?.docs?.filter((c) => c.isCompleted)?.length > 0 &&
              transactionsData?.result?.docs.filter((c) => c.isCompleted).map((campaign) => (
                <Card
                  key={campaign._id}
                  className="bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-colors"
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white text-base md:text-lg">
                        {campaign.campaignId?.name}
                      </h3>
                      {campaign.isCompleted && !campaign.isClaimed ? (
                        <Badge className="bg-green-500 text-white">
                          Ready to Claim
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-500 text-white">
                          In Progress
                        </Badge>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Progress</span>
                        <span className="text-white">
                          {campaign.allVerifiedTasks}/{campaign.allTasks} tasks
                        </span>
                      </div>
                      <Progress
                        value={
                          (campaign.allVerifiedTasks / campaign.allTasks) * 100
                        }
                        className="h-2 bg-gray-800"
                      >
                        <div
                          className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all"
                          style={{
                            width: `${
                              (campaign.allVerifiedTasks / campaign.allTasks) *
                              100
                            }%`,
                          }}
                        />
                      </Progress>
                    </div>

                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-white">
                          {campaign.totalReward} BINGOLD
                        </div>
                        <div className="text-gray-400 text-sm">
                          Potential Reward
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white">
                          {
                            getTimeRemaining(
                              campaign.campaignId?.startDate,
                              campaign.campaignId?.endDate,
                            ).days
                          }{" "}
                          day
                        </div>
                        <div className="text-gray-400 text-sm">Time Left</div>
                      </div>
                    </div>

                    {campaign.isCompleted && !campaign?.isClaimed ? (
                      <Button
                        className="w-full cursor-pointer bg-gradient-to-r from-green-400 to-green-600 text-black hover:from-green-500 hover:to-green-700"
                        onClick={() => claimRewards(campaign.campaignId?._id)}
                        disabled={isClaiming}
                      >
                        {isClaiming ? "Claiming..." : "Claim Rewards"}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Link
                        to={`${DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN}/${campaign.campaignId?._id}`}
                      >
                        <Button
                          className="w-full cursor-pointer bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700"
                        >
                          Continue Tasks
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
          </div>

          {(transactionsError ||
            transactionsData?.result?.docs?.filter((c) => c.isCompleted)?.length === 0) && (
            <div>
              <p>No Active Campaigns Found</p>
            </div>
          )}
        </div>
      </div>

      {/* Recommended Campaigns */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl text-white">
            Recommended For You
          </h2>
          <Link to={DASHBOARD_PATH?.RECOMMENDED_CAMPAIGNS}>
            <Button
              variant="outline"
              className="cursor-pointer border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black mt-4 md:mt-0"
            >
              Explore More
            </Button>
          </Link>
        </div>

        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {recommendedCampaignsData?.result &&
              recommendedCampaignsData?.result?.length > 0 &&
              recommendedCampaignsData?.result.map((campaign) => (
                <Card
                  key={campaign._id}
                  className="bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-colors"
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white text-base md:text-lg">
                        {campaign.name}
                      </h3>
                      <Badge
                        variant="outline"
                        className="border-yellow-500 text-yellow-400"
                      >
                        New
                      </Badge>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-white">
                          {campaign.airdropAmount}
                        </div>
                        <div className="text-gray-400 text-xs">BINGOLD</div>
                      </div>
                      <div className="text-center">
                        <div className="text-white">{campaign.totalTasks}</div>
                        <div className="text-gray-400 text-xs">Tasks</div>
                      </div>
                      <div className="text-center">
                        <div className="text-white">{campaign.userJoined}</div>
                        <div className="text-gray-400 text-xs">Joined</div>
                      </div>
                    </div>
                    <Link
                      to={`${DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN}/${campaign._id}`}
                    >
                      <Button
                        variant="outline"
                        className="w-full border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black"
                      >
                        Start Campaign
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
          </div>

          {(recommendedCampaignsError ||
            recommendedCampaignsData?.result?.length === 0) && (
            <div className="mt-4">
              <p>No Recommended Campaigns Found!</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      {/* <Card className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-500/20">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-white text-base md:text-lg mb-2">
                Need Help Getting Started?
              </h3>
              <p className="text-gray-300">
                Connect your social accounts to unlock more campaigns and earn
                higher rewards.
              </p>
            </div>
            <Button className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 flex-shrink-0">
              Connect Accounts
            </Button>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
}
